import { useState, useEffect } from "react";
import axios from "axios";
import { saveWithRetry, errorMessage } from "@/lib/saveWithRetry";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { 
  Search, 
  Package, 
  Plus, 
  Trash2, 
  ClipboardCheck, 
  History,
  RefreshCw,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Save,
  X
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const ADJUSTMENT_TYPES = [
  { value: "COUNT_VARIANCE", label: "Count Variance", description: "Physical count differs from system" },
  { value: "BREAKAGE", label: "Breakage/Damage", description: "Stock damaged or broken" },
  { value: "THEFT", label: "Theft/Shrinkage", description: "Stock missing due to theft" },
  { value: "FOUND_STOCK", label: "Found Stock", description: "Stock found that wasn't in system" },
  { value: "EXPIRY", label: "Expiry Write-off", description: "Stock expired and written off" },
  { value: "WRITE_OFF", label: "General Write-off", description: "Stock written off for other reasons" },
  { value: "OTHER", label: "Other", description: "Other adjustment reasons" }
];

// Format currency
const formatCurrency = (value) => {
  return `R ${(parseFloat(value) || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
};

export default function StockTake() {
  const [activeTab, setActiveTab] = useState("new");
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  
  // New adjustment state
  const [adjustmentType, setAdjustmentType] = useState("COUNT_VARIANCE");
  const [reason, setReason] = useState("STOCK COUNT");
  const [notes, setNotes] = useState("");
  const [adjustmentItems, setAdjustmentItems] = useState([]);
  const [adjustedBy, setAdjustedBy] = useState("HERMAN");
  
  // History state
  const [adjustmentHistory, setAdjustmentHistory] = useState([]);
  const [selectedAdjustment, setSelectedAdjustment] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  
  // Confirmation dialog
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Fetch adjustment history
  const fetchHistory = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/stock-take/adjustments?limit=50`);
      setAdjustmentHistory(response.data);
    } catch (error) {
      console.error("Error fetching history:", error);
      toast.error("Failed to load adjustment history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "history") {
      fetchHistory();
    }
  }, [activeTab]);

  // Search products
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    try {
      const response = await axios.get(`${API}/products/search`, {
        params: { q: searchQuery, limit: 20 }
      });
      setSearchResults(response.data);
    } catch (error) {
      console.error("Error searching:", error);
      toast.error("Search failed");
    } finally {
      setSearching(false);
    }
  };

  // Add product to adjustment list
  const addProductToAdjustment = (product) => {
    // Check if already added
    if (adjustmentItems.find(item => item.product_id === product.id)) {
      toast.error("Product already added");
      return;
    }
    
    const cost = product.average_cost_price_excl_vat || product.last_cost_price_excl_vat || product.cost_price || 0;
    // For case products the search endpoint overwrites `current_stock` with
    // the number of full cases available. Stock-take is done in loose units,
    // so prefer `linked_stock` (the loose SKU's on-hand qty) whenever the
    // enrichment set it — otherwise a shelf with 6 loose muffins in stock
    // would appear as "0" just because they don't yet make up a full case.
    const isCase = product.is_case_product && product.linked_stock !== undefined && product.linked_stock !== null;
    const stockForCount = isCase
      ? Number(product.linked_stock) || 0
      : Number(product.current_stock) || 0;
    // Pick the correct target product id for the adjustment write —
    // adjusting a case shell doesn't move loose stock, but adjusting the
    // linked loose SKU does. Fall back to the case doc when there's no
    // linked id (defensive).
    const targetProductId = isCase && product.linked_product_id
      ? product.linked_product_id
      : product.id;
    const targetPlu = isCase && product.linked_product_name
      ? `${product.plu_code} (loose)`
      : product.plu_code;
    
    setAdjustmentItems([...adjustmentItems, {
      product_id: targetProductId,
      plu_code: targetPlu,
      product_name: product.name,
      system_stock: stockForCount,
      physical_count: stockForCount,
      variance: 0,
      unit_cost: cost,
      value_change: 0
    }]);
    
    setSearchQuery("");
    setSearchResults([]);
    toast.success(`Added ${product.name}`);
  };

  // Update item physical count
  const updatePhysicalCount = (index, value) => {
    const newItems = [...adjustmentItems];
    const physicalCount = parseFloat(value) || 0;
    const variance = physicalCount - newItems[index].system_stock;
    
    newItems[index] = {
      ...newItems[index],
      physical_count: physicalCount,
      variance: variance,
      value_change: variance * newItems[index].unit_cost
    };
    
    setAdjustmentItems(newItems);
  };

  // Remove item from adjustment
  const removeItem = (index) => {
    setAdjustmentItems(adjustmentItems.filter((_, i) => i !== index));
  };

  // Calculate totals
  const totalQtyChange = adjustmentItems.reduce((sum, item) => sum + item.variance, 0);
  const totalValueChange = adjustmentItems.reduce((sum, item) => sum + item.value_change, 0);

  // Submit adjustment
  const handleSubmit = async () => {
    if (adjustmentItems.length === 0) {
      toast.error("Please add at least one product");
      return;
    }
    
    // Check if any items have zero variance
    const hasChanges = adjustmentItems.some(item => item.variance !== 0);
    if (!hasChanges) {
      toast.error("No stock changes to record. Please adjust physical counts.");
      return;
    }
    
    setConfirmDialogOpen(true);
  };

  const confirmSubmit = async () => {
    setSubmitting(true);
    try {
      const response = await saveWithRetry(() => axios.post(`${API}/stock-take/adjustments`, {
        adjustment_type: adjustmentType,
        reason: reason || ADJUSTMENT_TYPES.find(t => t.value === adjustmentType)?.description,
        notes: notes,
        items: adjustmentItems,
        adjusted_by: adjustedBy || "System"
      }));
      
      toast.success(`Adjustment ${response.data.adjustment_number} created successfully`);
      
      // Reset form (keep defaults for next adjustment)
      setAdjustmentItems([]);
      setReason("STOCK COUNT");
      setNotes("");
      setAdjustedBy("HERMAN");
      setConfirmDialogOpen(false);
      
      // Switch to history tab
      setActiveTab("history");
      fetchHistory();
      
    } catch (error) {
      console.error("Error creating adjustment:", error);
      toast.error(errorMessage(error, "Failed to create adjustment"));
    } finally {
      setSubmitting(false);
    }
  };

  // View adjustment details
  const viewAdjustment = async (adjustmentId) => {
    try {
      const response = await axios.get(`${API}/stock-take/adjustments/${adjustmentId}`);
      setSelectedAdjustment(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      console.error("Error fetching adjustment:", error);
      toast.error("Failed to load adjustment details");
    }
  };

  // Void adjustment
  const voidAdjustment = async (adjustmentId) => {
    if (!confirm("Are you sure you want to void this adjustment? Stock levels will be reversed.")) {
      return;
    }
    
    try {
      await axios.delete(`${API}/stock-take/adjustments/${adjustmentId}`);
      toast.success("Adjustment voided successfully");
      fetchHistory();
      setViewDialogOpen(false);
    } catch (error) {
      console.error("Error voiding adjustment:", error);
      toast.error(error.response?.data?.detail || "Failed to void adjustment");
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Stock Take</h1>
          <p className="text-gray-500">Manage stock adjustments and physical counts</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="new" className="flex items-center gap-2">
            <ClipboardCheck className="h-4 w-4" />
            New Adjustment
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <History className="h-4 w-4" />
            History
          </TabsTrigger>
        </TabsList>

        {/* New Adjustment Tab */}
        <TabsContent value="new" className="space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            {/* Left: Adjustment Details */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Adjustment Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Adjustment Type</Label>
                  <Select value={adjustmentType} onValueChange={setAdjustmentType}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ADJUSTMENT_TYPES.sort((a, b) => (a.label || '').localeCompare(b.label || '')).map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          <div>
                            <div className="font-medium">{type.label}</div>
                            <div className="text-xs text-gray-500">{type.description}</div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Adjusted By</Label>
                  <Input 
                    value={adjustedBy}
                    onChange={(e) => setAdjustedBy(e.target.value)}
                    placeholder="Enter name"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label>Reason</Label>
                  <Input 
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Brief reason for adjustment"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label>Notes</Label>
                  <Textarea 
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Additional notes..."
                    rows={3}
                    className="mt-1"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Middle: Product Search */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Add Products</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input 
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                      placeholder="Search by PLU, barcode, or name..."
                      className="pl-10"
                    />
                  </div>
                  <Button onClick={handleSearch} disabled={searching}>
                    {searching ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                  </Button>
                </div>
                
                {/* Search Results */}
                <div className="max-h-[400px] overflow-y-auto space-y-2">
                  {searchResults.map(product => (
                    <div 
                      key={product.id}
                      className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer flex justify-between items-center"
                      onClick={() => addProductToAdjustment(product)}
                    >
                      <div>
                        <div className="font-medium text-sm">{product.name}</div>
                        <div className="text-xs text-gray-500">
                          PLU: {product.plu_code} | Stock: {
                            // For case products the search endpoint overrides
                            // `current_stock` with the case count. When the
                            // counter is doing stock-take they need to know
                            // how many INDIVIDUAL units are on the shelf,
                            // not how many full cases those units make up —
                            // so prefer the linked loose stock when present.
                            product.is_case_product && product.linked_stock !== undefined && product.linked_stock !== null
                              ? product.linked_stock
                              : (product.current_stock || 0)
                          }
                          {product.is_case_product && product.linked_product_name
                            ? ` (loose: ${product.linked_product_name})`
                            : ''}
                        </div>
                      </div>
                      <Button size="sm" variant="ghost">
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  {searchResults.length === 0 && searchQuery && !searching && (
                    <p className="text-center text-gray-500 py-4">No products found</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Right: Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Adjustment Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Items to Adjust:</span>
                    <span className="font-semibold">{adjustmentItems.length}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Total Qty Change:</span>
                    <span className={`font-semibold ${totalQtyChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {totalQtyChange >= 0 ? '+' : ''}{totalQtyChange.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm border-t pt-3">
                    <span className="text-gray-600">Value Change:</span>
                    <span className={`font-bold text-lg ${totalValueChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {totalValueChange >= 0 ? '+' : ''}{formatCurrency(totalValueChange)}
                    </span>
                  </div>
                </div>
                
                <Button 
                  className="w-full mt-4 bg-purple-600 hover:bg-purple-700"
                  onClick={handleSubmit}
                  disabled={adjustmentItems.length === 0}
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Adjustment
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Adjustment Items Table */}
          {adjustmentItems.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Adjustment Items ({adjustmentItems.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="px-3 py-2 text-left">PLU</th>
                        <th className="px-3 py-2 text-left">Product</th>
                        <th className="px-3 py-2 text-right">System Stock</th>
                        <th className="px-3 py-2 text-center">Physical Count</th>
                        <th className="px-3 py-2 text-right">Variance</th>
                        <th className="px-3 py-2 text-right">Unit Cost</th>
                        <th className="px-3 py-2 text-right">Value Change</th>
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {adjustmentItems.map((item, index) => (
                        <tr key={index} className="border-b hover:bg-gray-50">
                          <td className="px-3 py-2 font-mono text-xs">{item.plu_code}</td>
                          <td className="px-3 py-2">{item.product_name}</td>
                          <td className="px-3 py-2 text-right">{item.system_stock.toFixed(2)}</td>
                          <td className="px-3 py-2">
                            <Input 
                              type="number"
                              value={item.physical_count}
                              onChange={(e) => updatePhysicalCount(index, e.target.value)}
                              className="w-24 text-right mx-auto"
                              step="0.01"
                            />
                          </td>
                          <td className={`px-3 py-2 text-right font-semibold ${
                            item.variance > 0 ? 'text-green-600' : item.variance < 0 ? 'text-red-600' : ''
                          }`}>
                            {item.variance > 0 ? '+' : ''}{item.variance.toFixed(2)}
                            {item.variance > 0 && <TrendingUp className="inline h-3 w-3 ml-1" />}
                            {item.variance < 0 && <TrendingDown className="inline h-3 w-3 ml-1" />}
                          </td>
                          <td className="px-3 py-2 text-right">{formatCurrency(item.unit_cost)}</td>
                          <td className={`px-3 py-2 text-right font-semibold ${
                            item.value_change > 0 ? 'text-green-600' : item.value_change < 0 ? 'text-red-600' : ''
                          }`}>
                            {item.value_change > 0 ? '+' : ''}{formatCurrency(item.value_change)}
                          </td>
                          <td className="px-3 py-2">
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => removeItem(index)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-sm">Adjustment History</CardTitle>
              <Button variant="outline" size="sm" onClick={fetchHistory}>
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
                </div>
              ) : adjustmentHistory.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No adjustments found</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="px-3 py-2 text-left">Adj #</th>
                        <th className="px-3 py-2 text-left">Date</th>
                        <th className="px-3 py-2 text-left">Type</th>
                        <th className="px-3 py-2 text-right">Items</th>
                        <th className="px-3 py-2 text-right">Qty Change</th>
                        <th className="px-3 py-2 text-right">Value Change</th>
                        <th className="px-3 py-2 text-left">Adjusted By</th>
                        <th className="px-3 py-2 text-center">Status</th>
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {adjustmentHistory.map((adj) => (
                        <tr key={adj.id} className="border-b hover:bg-gray-50">
                          <td className="px-3 py-2 font-mono text-xs font-semibold text-purple-600">{adj.adjustment_number}</td>
                          <td className="px-3 py-2">{new Date(adj.created_at).toLocaleDateString()}</td>
                          <td className="px-3 py-2">
                            <span className="px-2 py-1 bg-gray-100 rounded text-xs">
                              {ADJUSTMENT_TYPES.find(t => t.value === adj.adjustment_type)?.label || adj.adjustment_type}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right">{adj.items?.length || 0}</td>
                          <td className={`px-3 py-2 text-right font-semibold ${
                            adj.total_qty_change > 0 ? 'text-green-600' : adj.total_qty_change < 0 ? 'text-red-600' : ''
                          }`}>
                            {adj.total_qty_change > 0 ? '+' : ''}{adj.total_qty_change?.toFixed(2)}
                          </td>
                          <td className={`px-3 py-2 text-right font-semibold ${
                            adj.total_value_change > 0 ? 'text-green-600' : adj.total_value_change < 0 ? 'text-red-600' : ''
                          }`}>
                            {adj.total_value_change > 0 ? '+' : ''}{formatCurrency(adj.total_value_change)}
                          </td>
                          <td className="px-3 py-2">{adj.adjusted_by}</td>
                          <td className="px-3 py-2 text-center">
                            <span className={`px-2 py-1 rounded text-xs ${
                              adj.status === 'completed' ? 'bg-green-100 text-green-700' :
                              adj.status === 'voided' ? 'bg-red-100 text-red-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {adj.status}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            <Button size="sm" variant="ghost" onClick={() => viewAdjustment(adj.id)}>
                              View
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              Confirm Stock Adjustment
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-gray-600">
              You are about to create a stock adjustment that will modify {adjustmentItems.length} product(s).
            </p>
            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span>Type:</span>
                <span className="font-semibold">{ADJUSTMENT_TYPES.find(t => t.value === adjustmentType)?.label}</span>
              </div>
              <div className="flex justify-between">
                <span>Total Qty Change:</span>
                <span className={`font-semibold ${totalQtyChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {totalQtyChange >= 0 ? '+' : ''}{totalQtyChange.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Total Value Change:</span>
                <span className={`font-bold ${totalValueChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {totalValueChange >= 0 ? '+' : ''}{formatCurrency(totalValueChange)}
                </span>
              </div>
            </div>
            <p className="text-sm text-orange-600">
              ⚠️ This action will immediately update stock levels. Continue?
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialogOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={confirmSubmit} disabled={submitting} className="bg-purple-600 hover:bg-purple-700">
              {submitting ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <ClipboardCheck className="h-4 w-4 mr-2" />
                  Confirm Adjustment
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Adjustment Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              Adjustment Details - {selectedAdjustment?.adjustment_number}
            </DialogTitle>
          </DialogHeader>
          {selectedAdjustment && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Type:</span>
                  <div className="font-semibold">
                    {ADJUSTMENT_TYPES.find(t => t.value === selectedAdjustment.adjustment_type)?.label}
                  </div>
                </div>
                <div>
                  <span className="text-gray-500">Date:</span>
                  <div className="font-semibold">{new Date(selectedAdjustment.created_at).toLocaleString()}</div>
                </div>
                <div>
                  <span className="text-gray-500">Adjusted By:</span>
                  <div className="font-semibold">{selectedAdjustment.adjusted_by}</div>
                </div>
                <div>
                  <span className="text-gray-500">Status:</span>
                  <div>
                    <span className={`px-2 py-1 rounded text-xs ${
                      selectedAdjustment.status === 'completed' ? 'bg-green-100 text-green-700' :
                      selectedAdjustment.status === 'voided' ? 'bg-red-100 text-red-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {selectedAdjustment.status}
                    </span>
                  </div>
                </div>
              </div>
              
              {selectedAdjustment.reason && (
                <div className="text-sm">
                  <span className="text-gray-500">Reason:</span>
                  <div>{selectedAdjustment.reason}</div>
                </div>
              )}
              
              {selectedAdjustment.notes && (
                <div className="text-sm">
                  <span className="text-gray-500">Notes:</span>
                  <div className="bg-gray-50 p-2 rounded mt-1">{selectedAdjustment.notes}</div>
                </div>
              )}
              
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="px-3 py-2 text-left">PLU</th>
                      <th className="px-3 py-2 text-left">Product</th>
                      <th className="px-3 py-2 text-right">System</th>
                      <th className="px-3 py-2 text-right">Physical</th>
                      <th className="px-3 py-2 text-right">Variance</th>
                      <th className="px-3 py-2 text-right">Value Change</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedAdjustment.items?.map((item, index) => (
                      <tr key={index} className="border-t">
                        <td className="px-3 py-2 font-mono text-xs">{item.plu_code}</td>
                        <td className="px-3 py-2">{item.product_name}</td>
                        <td className="px-3 py-2 text-right">{item.system_stock?.toFixed(2)}</td>
                        <td className="px-3 py-2 text-right">{item.physical_count?.toFixed(2)}</td>
                        <td className={`px-3 py-2 text-right font-semibold ${
                          item.variance > 0 ? 'text-green-600' : item.variance < 0 ? 'text-red-600' : ''
                        }`}>
                          {item.variance > 0 ? '+' : ''}{item.variance?.toFixed(2)}
                        </td>
                        <td className={`px-3 py-2 text-right font-semibold ${
                          item.value_change > 0 ? 'text-green-600' : item.value_change < 0 ? 'text-red-600' : ''
                        }`}>
                          {item.value_change > 0 ? '+' : ''}{formatCurrency(item.value_change)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-50 font-semibold">
                      <td colSpan={4} className="px-3 py-2 text-right">Totals:</td>
                      <td className={`px-3 py-2 text-right ${
                        selectedAdjustment.total_qty_change > 0 ? 'text-green-600' : selectedAdjustment.total_qty_change < 0 ? 'text-red-600' : ''
                      }`}>
                        {selectedAdjustment.total_qty_change > 0 ? '+' : ''}{selectedAdjustment.total_qty_change?.toFixed(2)}
                      </td>
                      <td className={`px-3 py-2 text-right ${
                        selectedAdjustment.total_value_change > 0 ? 'text-green-600' : selectedAdjustment.total_value_change < 0 ? 'text-red-600' : ''
                      }`}>
                        {selectedAdjustment.total_value_change > 0 ? '+' : ''}{formatCurrency(selectedAdjustment.total_value_change)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
          <DialogFooter>
            {selectedAdjustment?.status === 'completed' && (
              <Button 
                variant="destructive" 
                onClick={() => voidAdjustment(selectedAdjustment.id)}
              >
                <X className="h-4 w-4 mr-2" />
                Void Adjustment
              </Button>
            )}
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
